/**
 * The Auth0 settings PHP publishes, read defensively.
 *
 * Pure functions of a config object, so they are exported and tested. `App\Auth\Assets` is the
 * writer.
 *
 * **Every scalar arrives as a string, and that is not a bug to work around silently.**
 * `wp_localize_script()` casts top-level scalars: `expiryMargin` reaches the loyalty bundle as
 * `'120'` and a PHP `true` reaches it as `'1'`. Measured on `localhost:8080`. So a boolean here is
 * read, never asserted — `if ( config.useRefreshTokens )` would be true for the string `'0'` as
 * well, and `'0'` is what a PHP `false` becomes if anyone ever nests it. The same rule is written
 * up in docs/subsystems/tracking.md and docs/subsystems/gutenberg-blocks.md.
 */

/** What the SDK is given when nothing is published. Every field empty means "do not start". */
export const NO_CONFIG = Object.freeze( {
	domain: '',
	clientId: '',
	audience: '',
	homeUrl: '',
	callbackUrl: '',
	loginUrl: '',
	cacheLocation: 'memory',
	useRefreshTokens: false,
	debug: false,
} );

/**
 * A published scalar read as a boolean.
 *
 * `wp_localize_script` gives us `'1'` for true and `''` for false, and a hand-written config or a
 * dev-mode inline script can give us a real boolean. Both are accepted; `'0'` and `'false'` are
 * refused, because those are what a nested PHP false looks like and reading them as true is the
 * failure this helper exists to prevent.
 *
 * @param {*} value The published value.
 * @return {boolean} Whether it means yes.
 */
export function truthy( value ) {
	if ( typeof value === 'boolean' ) {
		return value;
	}

	if ( typeof value === 'number' ) {
		return value === 1;
	}

	return value === '1' || value === 'true';
}

/**
 * Where the SDK keeps its token cache.
 *
 * **A per-environment decision, not a tunable, and the reason is a shared browser.**
 * `localstorage` is what makes the silent-auth iframe unnecessary, and it is the only thing that
 * works on `localhost`, where the frame is cross-site to the Auth0 custom domain and can never
 * succeed. But it persists a refresh token across a browser restart, keyed by
 * `client_id::audience::scope` and **never by user** — the SDK's key shape is not ours to change —
 * so on a shared machine that is the previous fan's credential left behind.
 *
 * Production therefore runs `memory`, which bounds the exposure to one document, and `localhost`
 * runs `localstorage`. PHP decides; this only refuses a value the SDK would reject.
 *
 * @param {*} value The published value.
 * @return {'memory'|'localstorage'} A location the SDK accepts.
 */
export function cacheLocation( value ) {
	return value === 'localstorage' ? 'localstorage' : 'memory';
}

/**
 * The published config, normalised.
 *
 * @param {Object|null|undefined} raw Usually `window.urcAuth0`.
 * @return {Object} A config with every field present and correctly typed.
 */
export function readConfig( raw ) {
	const source = raw && typeof raw === 'object' ? raw : {};
	const text = name => ( typeof source[ name ] === 'string' ? source[ name ].trim() : '' );

	return {
		domain: text( 'domain' )
			.replace( /^https?:\/\//i, '' )
			.replace( /\/+$/, '' ),
		clientId: text( 'clientId' ),
		audience: text( 'audience' ),
		homeUrl: text( 'homeUrl' ),
		callbackUrl: text( 'callbackUrl' ),
		// Published by App\Auth\Assets since day one and read by nothing until loginLinks.js
		// needed something to compare an anchor's href against. `logoutUrl` is published too and
		// is still unread: no control in this theme links to /auth/logout.
		loginUrl: text( 'loginUrl' ),
		cacheLocation: cacheLocation( source.cacheLocation ),
		useRefreshTokens: truthy( source.useRefreshTokens ),
		debug: truthy( source.debug ),
	};
}

/**
 * Whether verbose logging is on for this page load.
 *
 * Two ways in, and the query parameter is the one that matters: this subsystem's failures are
 * environment-shaped -- a cross-site iframe, a cookie a third party deleted, a tenant setting -- so
 * they are reproduced on somebody else's machine, on an install nobody wants to reconfigure. A URL
 * they can be handed is the difference between a diagnosis and a guessing game. It is what the
 * absence of one cost during the first live login on this flow: a broken token request looked
 * exactly like a working one.
 *
 * Logging only. It cannot change a decision, so it is safe to leave reachable in production, which
 * is the whole point -- the interesting failures do not happen locally.
 *
 * @param {Object} config   From `readConfig()`.
 * @param {string} [search] Defaults to the current query string.
 * @return {boolean} Whether to log.
 */
export function isDebug( config, search ) {
	if ( config.debug ) {
		return true;
	}

	const query = typeof search === 'string' ? search : globalSearch();

	return /[?&]auth_debug=1(&|$)/.test( query );
}

/**
 * The current query string, or ''.
 *
 * @return {string} The query string.
 */
function globalSearch() {
	if ( typeof window === 'undefined' || ! window.location ) {
		return '';
	}

	return window.location.search || '';
}

/**
 * Whether there is enough to start an SDK client at all.
 *
 * Three strings, and a missing one means the environment has no Auth0 configured — which is a
 * legitimate state for a fresh clone and for any install where `AUTH0_DOMAIN` is unset. The caller
 * publishes nothing and every consumer reads "signed out", which is exactly the pre-Auth0
 * behaviour.
 *
 * @param {Object} config From `readConfig()`.
 * @return {boolean} True when a client can be built.
 */
export function isConfigured( config ) {
	return config.domain !== '' && config.clientId !== '' && config.callbackUrl !== '';
}
